import React, { useEffect, useMemo, useState } from 'react';

import { BlockStack, Box, Listbox, Scrollable, Select, Text, TextField } from '@shopify/polaris';
import AppModal from './AppModal';
import useAdminApi from '../hooks/Api';

const INHERIT = '';
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Picks any Shopify category and the article code it should report, so an exception can
 * go deeper than the categories Planet split by default.
 *
 * `onAdd` should resolve true once the mapping is saved.
 */
function AddCategoryExceptionModal({ id, codes, onAdd }) {
    const { request } = useAdminApi();

    const [search, setSearch] = useState('');
    const [matches, setMatches] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [pickedGid, setPickedGid] = useState(null);
    const [pickedCode, setPickedCode] = useState(INHERIT);
    const [isAdding, setIsAdding] = useState(false);

    // Search the taxonomy as the merchant types.
    // `request` is rebuilt on every render by useAdminApi, so it must stay out of the
    // dependencies — including it re-runs this effect constantly and the cleanup clears
    // each timer before it can fire, leaving the spinner up and the search never running.
    useEffect(() => {
        // A selection just wrote the full path into the field; nothing to look up.
        if (pickedGid) {
            setIsSearching(false);

            return;
        }

        if (search.trim().length < MIN_SEARCH_LENGTH) {
            setMatches([]);
            setIsSearching(false);

            return;
        }

        setIsSearching(true);

        const timer = setTimeout(async () => {
            const { ok, data: found } = await request(
                `category-mappings/categories?q=${encodeURIComponent(search.trim())}`,
            );

            setMatches(ok ? found : []);
            setIsSearching(false);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [search, pickedGid]);

    // Full paths here — picking the right category depends on seeing where it sits.
    const searchOptions = useMemo(() => matches.map((match) => ({ value: match.gid, label: match.path })), [matches]);

    async function addException() {
        if (!pickedGid || pickedCode === INHERIT) return;

        setIsAdding(true);
        const saved = await onAdd(pickedGid, pickedCode);
        setIsAdding(false);

        // Leave the modal open on failure so the merchant keeps their selection and can retry.
        if (!saved) return;

        setSearch('');
        setMatches([]);
        setPickedGid(null);
        setPickedCode(INHERIT);
        shopify.modal.hide(id);
    }

    function clearSelection(value = '') {
        setSearch(value);
        setPickedGid(null);
    }

    const showNoResults =
        !pickedGid && !isSearching && search.trim().length >= MIN_SEARCH_LENGTH && searchOptions.length === 0;

    return (
        <AppModal
            id={id}
            title="Add a sub-category exception"
            cancelLabel="Cancel"
            confirmLabel="Add exception"
            confirmAction={addException}
            confirmEnabled={Boolean(pickedGid) && pickedCode !== INHERIT}
            processing={isAdding}
        >
            <BlockStack gap="400">
                {/* Results render inline rather than through Autocomplete. App Bridge portals this
                    modal's children into its own container, while a Polaris Popover portals to the
                    main app tree — so a Combobox dropdown opens outside the modal and is never seen. */}
                <BlockStack gap="200">
                    <TextField
                        label="Sub-category"
                        value={search}
                        placeholder="Search Shopify categories"
                        autoComplete="off"
                        loading={isSearching}
                        clearButton
                        onClearButtonClick={() => clearSelection()}
                        onChange={clearSelection}
                    />

                    {!pickedGid && searchOptions.length > 0 && (
                        <Box borderWidth="025" borderColor="border" borderRadius="200" paddingBlock="100">
                            <Scrollable horizontal={false} style={{ maxHeight: '220px' }}>
                                <Listbox
                                    onSelect={(gid) => {
                                        setPickedGid(gid);
                                        setSearch(searchOptions.find((option) => option.value === gid)?.label ?? '');
                                    }}
                                >
                                    {searchOptions.map((option) => (
                                        <Listbox.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Listbox.Option>
                                    ))}
                                </Listbox>
                            </Scrollable>
                        </Box>
                    )}

                    {showNoResults && (
                        <Text tone="subdued" as="p">
                            No categories match that search.
                        </Text>
                    )}
                </BlockStack>

                <Select
                    label="Article code"
                    options={[{ value: INHERIT, label: 'Select an article code' }, ...codes]}
                    value={pickedCode}
                    onChange={setPickedCode}
                />
            </BlockStack>
        </AppModal>
    );
}

export default AddCategoryExceptionModal;
